// The pure half of the Google Analytics tag: which pages may carry it, what
// address it is told a page has, which events exist, and which values may ride
// on them. `boot.ts` is the only module that loads the tag; everything with a
// decision in it is here, where the unit suite can read it as a table.
//
// THE SHAPE OF THE INTEGRATION, stated once:
//
//   - The tag is loaded ONLY after the visitor has allowed the measurement
//     category, and never before. Nothing from Google is requested on a visit
//     that has not said yes — not the script, not a consent ping. That is the
//     "basic" way of wiring consent to the tag, chosen over the "advanced" one
//     (which loads the tag first and sends cookieless pings on refusal) because
//     a refusal here has to mean that no request reaches Google at all.
//   - Consent is still declared to the tag, defaults denied, before its first
//     command, and then only the analytics signal is granted. The three
//     advertising signals are never granted: this site runs no advertising.
//   - The tag's own page views are switched off. Every page view is sent here,
//     with the address reduced the same way the first-party tracker reduces it,
//     plus opaque ids in the path collapsed, so no query string that could be a
//     one-time credential and no row id ever reaches a report.
//   - Every event and every parameter is on a list below. A name that is not
//     on it is not sent, a value that fails its rule is not sent, and any string
//     that looks like an email address or a document line is refused outright,
//     wherever it appears.
//
// Browser-safe: no framework import, no Node built-in.

import { splitLocale } from "../i18n/locales.ts";
import { isEventName, toMeasurementPath } from "./tracker.ts";

/** The attribute the two document templates carry the measurement ID on. */
export const GA_ID_ATTRIBUTE = "data-ga-id";

/** The environment variable both templates read it from. Public, not a secret. */
export const GA_ENV_NAME = "PUBLIC_GA_MEASUREMENT_ID";

/** Where the tag is served from — the one third-party script origin it adds. */
export const GA_SCRIPT_ORIGIN = "https://www.googletagmanager.com";

const MEASUREMENT_ID = /^G-[A-Z0-9]{4,20}$/;

/**
 * The measurement ID a template carried, or null when there is none.
 *
 * Null for an absent or empty attribute AND for anything that is not shaped
 * like an ID: the value becomes part of a script address, and a template that
 * was handed garbage should load nothing rather than something.
 */
export function readMeasurementId(value: string | null | undefined): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim().toUpperCase();
  return MEASUREMENT_ID.test(trimmed) ? trimmed : null;
}

/** The tag's own address for a measurement ID. */
export function gaScriptSrc(measurementId: string): string {
  return `${GA_SCRIPT_ORIGIN}/gtag/js?id=${encodeURIComponent(measurementId)}`;
}

/**
 * Where the tag is never loaded and never sent anything from.
 *
 * The administrator console renders other people's accounts and is our own
 * traffic besides; the error probe is a diagnostic; the API paths are not pages.
 * The cabinet IS measured — how people use it is exactly what the measurement
 * is for — but its addresses are reduced before they leave (`toGaPageLocation`).
 */
export const GA_DENIED_PREFIXES: readonly string[] = ["/admin", "/error-probe", "/api"];

export function gaAllowedOn(pathname: string): boolean {
  if (typeof pathname !== "string" || pathname === "") return false;
  const path = splitLocale(pathname).path;
  return !GA_DENIED_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

// A path segment that names a row rather than a page: a UUID, or a long run of
// hex or digits. Collapsed so `/app/scans/<id>` is one page in a report rather
// than one page per scan, and so no row id of ours is sent anywhere.
const OPAQUE_SEGMENT =
  /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{16,}|\d{6,})$/i;

/** A path with every opaque id segment replaced by `:id`. */
export function collapseIds(path: string): string {
  return path
    .split("/")
    .map((segment) => (OPAQUE_SEGMENT.test(segment) ? ":id" : segment))
    .join("/");
}

/**
 * A page address as the tag may see it: this origin, the path with ids
 * collapsed, and only the campaign parameters of the query — the first-party
 * tracker's own rule (`toMeasurementPath`) with one more step.
 */
export function toGaPageLocation(rawUrl: string, origin: string): string {
  const reduced = toMeasurementPath(rawUrl, origin);
  const queryAt = reduced.indexOf("?");
  const path = queryAt === -1 ? reduced : reduced.slice(0, queryAt);
  const query = queryAt === -1 ? "" : reduced.slice(queryAt);
  return `${origin}${collapseIds(path)}${query}`;
}

/** What the tag is told before its first command: everything denied. */
export const CONSENT_DEFAULTS = {
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
  analytics_storage: "denied",
} as const;

/** What an allowed measurement category grants. Advertising stays denied. */
export const CONSENT_GRANTED = { analytics_storage: "granted" } as const;

/** What a withdrawal sets. */
export const CONSENT_WITHDRAWN = { analytics_storage: "denied" } as const;

/** The value of `traffic_type` the property's data filter excludes. */
export const INTERNAL_TRAFFIC = "internal";

export interface GaIdentity {
  /** The account's analytics pseudonym, or null for a visitor not signed in. */
  readonly userId: string | null;
  /** Our own people — administrators, and anybody viewing another account. */
  readonly internal: boolean;
}

export const ANONYMOUS: GaIdentity = { userId: null, internal: false };

const USER_ID = /^[0-9a-f]{32}$/;

/** An identity as the page stated it, or anonymous if it is not well formed. */
export function readIdentity(value: unknown): GaIdentity {
  if (typeof value !== "object" || value === null) return ANONYMOUS;
  const candidate = value as { userId?: unknown; internal?: unknown };
  const userId =
    typeof candidate.userId === "string" && USER_ID.test(candidate.userId)
      ? candidate.userId
      : null;
  return { userId, internal: candidate.internal === true };
}

/**
 * The configuration the tag is given.
 *
 * `send_page_view: false` because page views are sent deliberately, with the
 * reduced address.
 *
 * NO `page_location` HERE, deliberately. A value given to `config` outranks one
 * given later with `set`, so an address stated here would stay pinned to the
 * page the tag started on: after a move through the client router every event
 * the tag sends — scrolls, clicks, this app's own — would be filed under that
 * first page. Measured, not reasoned: a scan finished on the landing page was
 * reported against the cabinet page the tag had been configured on. The
 * address is stated with `set` and on every event instead (boot.ts). Google signals and ad personalisation are refused here as
 * well as in the property's settings, so neither depends on a dashboard switch.
 * `debug_mode` is only ever present when asked for: the vendor documents that
 * setting it to false does not turn it off.
 */
export function gaConfig(input: {
  readonly identity: GaIdentity;
  readonly debug: boolean;
}): Record<string, string | boolean> {
  return {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    ...(input.identity.userId === null ? {} : { user_id: input.identity.userId }),
    ...(input.identity.internal ? { traffic_type: INTERNAL_TRAFFIC } : {}),
    ...(input.debug ? { debug_mode: true } : {}),
  };
}

// ---------------------------------------------------------------------------
// Events.

type Rule = (value: unknown) => boolean;

const oneOf =
  (...allowed: readonly string[]): Rule =>
  (value) =>
    typeof value === "string" && allowed.includes(value);

const matches =
  (pattern: RegExp): Rule =>
  (value) =>
    typeof value === "string" && pattern.test(value);

const amount: Rule = (value) =>
  typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= 100_000;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The scan outcomes the product reports, plus a call that failed outright. */
export const SCAN_OUTCOMES = [
  "recognized",
  "no_document_found",
  "unreadable",
  "unsupported_document",
  "rejected",
  "error",
] as const;

/**
 * Every event this site sends to Google Analytics, and every parameter each
 * one may carry with the rule its value has to pass.
 *
 * Recommended event names where the vendor has one (`sign_up`, `login`,
 * `begin_checkout`, `purchase`), so its standard reports understand them.
 * Every custom parameter here is registered as a custom dimension in the
 * property; adding one is an entry here plus that registration.
 */
export const GA_EVENTS: Readonly<Record<string, Readonly<Record<string, Rule>>>> = {
  // A marked control was clicked: the landing and pricing calls to action, the
  // navigation. The id is the element's own `data-analytics-id`.
  cta_click: { cta_id: (value) => typeof value === "string" && isEventName(value) },
  language_switch: { language: matches(/^[a-z]{2}(?:-[a-z0-9]{2,8})?$/) },
  copy_code: { surface: oneOf("docs", "demo") },
  sign_up: { method: oneOf("email", "google") },
  login: { method: oneOf("email", "google") },
  email_verified: {},
  api_key_created: { key_kind: oneOf("live", "sandbox") },
  // The OUTCOME of a recognition and where it was asked for. Never a field,
  // never a document type, never anything read off the image.
  scan_completed: { scan_source: oneOf("demo", "cabinet"), scan_status: oneOf(...SCAN_OUTCOMES) },
  begin_checkout: {
    currency: oneOf("USD"),
    value: amount,
    payment_asset: matches(/^[a-z0-9_]{2,20}$/),
  },
  purchase: {
    currency: oneOf("USD"),
    value: amount,
    transaction_id: (value) => typeof value === "string" && UUID.test(value),
    payment_asset: matches(/^[a-z0-9_]{2,20}$/),
  },
  // An error a person was shown. A status or a stable code, never the message.
  error_shown: {
    error_code: matches(/^[a-z0-9_.-]{1,40}$/),
    error_surface: oneOf("page", "demo", "upload", "form"),
  },
};

// What a personal value looks like, refused wherever it appears — even inside
// a value its rule would otherwise let through. An email address, a run of
// digits long enough to be a phone or document number, and the filler of a
// machine-readable zone.
const EMAIL = /[^\s@]+@[^\s@]+\.[^\s@]+/;
const LONG_DIGITS = /\d{9,}/;
const MRZ_FILLER = /<</;

export function looksPersonal(value: unknown): boolean {
  if (typeof value !== "string") return false;
  // A UUID is one of our own row ids — the only long-digit shape a listed
  // parameter carries on purpose (`transaction_id`) — and its last group can
  // be twelve digits by chance.
  if (UUID.test(value)) return false;
  return EMAIL.test(value) || LONG_DIGITS.test(value) || MRZ_FILLER.test(value);
}

export type GaParams = Readonly<Record<string, string | number>>;

/**
 * An event reduced to what may be sent, or null when it may not be sent at all.
 *
 * An unknown event is null. A parameter that is not on the event's list is
 * dropped. A parameter that fails its rule is dropped. And if anything at all
 * looks personal — a listed value or not — the WHOLE event is refused, because
 * a caller that passed one personal value is a caller whose other values are
 * not to be trusted either.
 */
export function sanitizeEvent(
  name: string,
  params: Readonly<Record<string, unknown>> = {},
): { name: string; params: GaParams } | null {
  const rules = Object.hasOwn(GA_EVENTS, name) ? GA_EVENTS[name] : undefined;
  if (rules === undefined) return null;
  if (Object.values(params).some(looksPersonal)) return null;
  const kept: Record<string, string | number> = {};
  for (const [key, rule] of Object.entries(rules)) {
    const value = params[key];
    if (value !== undefined && rule(value)) kept[key] = value as string | number;
  }
  return { name, params: kept };
}

/**
 * The event a click on a marked control becomes.
 *
 * Two families have events of their own: the language switcher, whose id ends
 * in the language chosen, and the documentation's copy button. Everything else
 * is a `cta_click` carrying the id, which is already a bounded, lowercase
 * coordinate (`area.element.action`) and nothing a person typed.
 */
export function clickEvent(analyticsId: string): { name: string; params: GaParams } | null {
  if (!isEventName(analyticsId)) return null;
  const language = /^language\.switch\.(.+)$/.exec(analyticsId);
  if (language) return sanitizeEvent("language_switch", { language: language[1] });
  if (analyticsId === "docs.code.copy") return sanitizeEvent("copy_code", { surface: "docs" });
  return sanitizeEvent("cta_click", { cta_id: analyticsId });
}
